import Header from "../components/common/header";
import Footer from "../components/common/footer";
import { Activity, Zap, Layers, Cpu, ShieldCheck } from "lucide-react";

export default function SignalAnatomy() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--black)",
        color: "var(--white)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />

      <main
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: "64px 24px",
          flex: 1,
        }}
      >
        {/* HERO */}
        <h1
          style={{
            fontSize: 42,
            fontWeight: 700,
            marginBottom: 12,
            color: "var(--primary)",
          }}
        >
          Signal Anatomy
        </h1>

        <p style={{ color: "var(--grey)", fontSize: 17, maxWidth: 720 }}>
          A structured view into how raw on-chain activity is transformed into
          actionable real-time intelligence.
        </p>

        {/* SECTION */}
        <section style={{ marginTop: 56 }}>
          <SectionTitle icon={<Activity />} title="What is a Signal?" />

          <p style={text}>
            A signal is not a transaction. A signal is a classified interpretation
            of on-chain behavior, derived from live mempool and block data.
          </p>

          <ul style={list}>
            <li>Raw transaction → decoded</li>
            <li>Context applied (method, value, flow, timing)</li>
            <li>Behavior classified into a signal type</li>
          </ul>
        </section>

        <section style={{ marginTop: 56 }}>
          <SectionTitle icon={<Layers />} title="Signal Categories" />

          <Grid>
            <Card title="Structural">
              Contract calls, interactions, approvals, deployments.
            </Card>
            <Card title="Value Flow">
              ERC20 transfers, swap amounts, large value movements.
            </Card>
            <Card title="Behavioral">
              Bot-like patterns, complex execution, MEV-style activity.
            </Card>
            <Card title="Risk & Anomalies">
              Gas spikes, zero-value logic calls, abnormal execution.
            </Card>
          </Grid>
        </section>

        <section style={{ marginTop: 56 }}>
          <SectionTitle icon={<Cpu />} title="Why This Matters" />

          <p style={text}>
            Most platforms show delayed, aggregated data. Our system operates
            before execution, inside the mempool, where intent is still forming.
          </p>

          <ul style={list}>
            <li>Early detection of market intent</li>
            <li>MEV-aware execution context</li>
            <li>Foundation for automated strategies</li>
          </ul>
        </section>

        <section style={{ marginTop: 56 }}>
          <SectionTitle icon={<ShieldCheck />} title="Infrastructure First" />

          <p style={text}>
            Signals are a byproduct of infrastructure, not a UI feature. The same
            data feeds execution engines, analytics, and future protocol layers.
          </p>
        </section>

        {/* CTA */}
        <div
          style={{
            marginTop: 72,
            padding: 24,
            border: "1px solid #222",
            borderRadius: 10,
            background: "#0b0b0b",
          }}
        >
          <h3 style={{ marginBottom: 8, color: "var(--primary)" }}>
            Live Data, Not Mockups
          </h3>
          <p style={{ color: "var(--grey)", marginBottom: 16 }}>
            All signals shown across the platform are generated in real time by
            the same production pipeline.
          </p>

          <a
            href="/signals"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 18px",
              borderRadius: 8,
              border: "1px solid var(--primary)",
              color: "var(--primary)",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            <Zap size={16} />
            View Live Signals
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
}

/* ===== helpers ===== */

function SectionTitle({ icon, title }) {
  return (
    <h2
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontSize: 24,
        marginBottom: 16,
        color: "var(--primary)",
      }}
    >
      {icon}
      {title}
    </h2>
  );
}

function Grid({ children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 16,
      }}
    >
      {children}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div
      style={{
        padding: 16,
        borderRadius: 10,
        border: "1px solid #222",
        background: "#0c0c0c",
      }}
    >
      <strong style={{ color: "var(--primary)" }}>{title}</strong>
      <p style={{ color: "var(--grey)", marginTop: 8 }}>{children}</p>
    </div>
  );
}

const text = {
  color: "var(--white)",
  lineHeight: 1.6,
  maxWidth: 820,
};

const list = {
  marginTop: 12,
  paddingLeft: 18,
  color: "var(--grey)",
};